import {
  CrossFieldObject,
  CrossFieldValidationJson,
  DARTObject,
  DropDownOption,
  FormCollectionSet
} from 'src/interfaces/utility-interfaces';

import { AbstractControlWarn } from './input-control.service';
import { AppConstants } from 'src/constants/constants';
import { BaseInput } from '../shared/models/base-input';
import { DateTimeInput } from '../shared/models/date-time-input';
import { FormGroup } from '@angular/forms';
import { Injectable } from '@angular/core';
import { UtilService } from './util.service';

@Injectable({
  providedIn: 'root'
})
export class ErrorService {

  constructor(private utilService: UtilService) { }

  checkDate(form: FormGroup, formData: BaseInput<any>[]) {
    for (const field of formData) {
      if (field.controlType === 'datepicker') {
        const fieldValue = form.get(field.linkId).value;
        if (fieldValue) {
          const parsedDate = new Date(fieldValue);
          if (fieldValue.length !== 10 || parsedDate.toUTCString() === 'Invalid Date' ||
            !this.utilService.checkValidDate(parsedDate, fieldValue)) {
            form.get(field.linkId).setErrors(
              { 'The value you have entered is invalid. The value must be a date in the format YYYY-MM-DD.': true });
            field.errorMsg = this.setFieldError(form, field);
          } else {
            form.get(field.linkId).updateValueAndValidity();
          }
        }
        field.invalid = this.setInvalid(form, field);
      }
    }
  }

  isDARTsValid(formCollection: FormCollectionSet) {
    const DARTObjList = this.getDARTObjList(formCollection);
    if (DARTObjList.length === 0) {
      return true;
    }

    const matchingDARTFields = this.checkMatchInDART(DARTObjList);
    if (matchingDARTFields?.length > 0) {
      return false;
    }

    const withinPeriodBookingFields =
        this.checkDateInDART(formCollection, DARTObjList, 'acceptance', AppConstants.bookingDateFieldLinkId);
    if (withinPeriodBookingFields?.length > 0) {
      return false;
    }
    const withinPeriodRemovalFields =
        this.checkDateInDART(formCollection, DARTObjList, 'offlisting', AppConstants.removalDateFieldLinkId);
    if (withinPeriodRemovalFields?.length > 0) {
      return false;
    }
    return true;
  }

  checkDARTDates(formCollection: FormCollectionSet) {
    const DARTObjList = this.getDARTObjList(formCollection);

    if (DARTObjList.length > 0) {
      const matchingDARTFields = this.checkMatchInDART(DARTObjList);
      const dupDARTErrorMsg = 'The values you have entered cannot be equal to another Dates Affecting Readiness to Treat period.';
      this.removeErrorForFormSection('acceptance', formCollection, dupDARTErrorMsg);
      this.showErrorForFields(matchingDARTFields, formCollection, dupDARTErrorMsg);

      const withinPeriodBookingFields =
        this.checkDateInDART(formCollection, DARTObjList, 'acceptance', AppConstants.bookingDateFieldLinkId);
      const periodDARTErrorMsg = 'The value you have entered cannot be within a Dates Affecting Readiness to Treat period.';
      this.removeErrorForFormSection('acceptance', formCollection, periodDARTErrorMsg);
      this.showErrorForFields(withinPeriodBookingFields, formCollection, periodDARTErrorMsg);

      const withinPeriodRemovalFields =
        this.checkDateInDART(formCollection, DARTObjList, 'offlisting', AppConstants.removalDateFieldLinkId);
      this.removeErrorForFormSection('offlisting', formCollection, periodDARTErrorMsg);
      this.showErrorForFields(withinPeriodRemovalFields, formCollection, periodDARTErrorMsg);
    }
  }

  getDARTObjList(formCollection: FormCollectionSet): DARTObject[] {
    const returnList = [];
    let otherDARTObj = {} as DARTObject;
    let otherDARTsuffix = null;

    Object.keys(formCollection.acceptance.form.value).forEach(fieldKey => {
      if (fieldKey.includes(AppConstants.DARTFieldLinkId)) {
        const parsedFieldKey = fieldKey.split('-');
        const fieldKeySuffix = +parsedFieldKey[parsedFieldKey.length - 1] || null;

        if (otherDARTsuffix !== fieldKeySuffix) {
          returnList.push(otherDARTObj);
          otherDARTObj = {} as DARTObject;
          otherDARTsuffix = fieldKeySuffix;
        }

        if (fieldKeySuffix && parsedFieldKey[parsedFieldKey.length - 2] === 'date') {
          otherDARTObj[parsedFieldKey[parsedFieldKey.length - 3]] = formCollection.acceptance.form.value[fieldKey];
        } else if (!fieldKeySuffix && parsedFieldKey[parsedFieldKey.length - 1] === 'date') {
          otherDARTObj[parsedFieldKey[parsedFieldKey.length - 2]] = formCollection.acceptance.form.value[fieldKey];
        } else {
          otherDARTObj.title = fieldKey;
        }
      }
    });
    if (Object.keys(otherDARTObj).length !== 0) {
      returnList.push(otherDARTObj);
    }
    return returnList;
  }

  checkMatchInDART(DARTObjList: DARTObject[]): string[] {
    const returnList = [];
    for (let i = 0; i < DARTObjList.length; i++) {
      for (let j = i + 1; j < DARTObjList.length; j++) {
        let match = true;
        Object.keys(DARTObjList[i]).forEach(pairKey => {
          if (pairKey !== 'title') {
            if (DARTObjList[i][pairKey] && DARTObjList[j][pairKey]) {
              match = match && (new Date(DARTObjList[i][pairKey]).getTime() === new Date(DARTObjList[j][pairKey]).getTime());
            } else {
              match = false;
            }
          }
        });

        if (match) {
          if (returnList.indexOf(DARTObjList[i].title) === -1) {
            returnList.push(DARTObjList[i].title);
          }
          if (returnList.indexOf(DARTObjList[j].title) === -1) {
            returnList.push(DARTObjList[j].title);
          }
        }
      }
    }
    return returnList;
  }

  checkDateInDART(formCollection: FormCollectionSet, DARTObjList: DARTObject[], section: string, fieldLinkId: string): string[] {
    const returnList = [];
    Object.keys(formCollection[section].form.value).forEach(fieldKey => {
      if (fieldKey.includes(fieldLinkId)) {
        const bookingDate = new Date(formCollection[section].form.value[fieldKey]).getTime();
        for (const DARTObj of DARTObjList) {
          const DARTFrom = new Date(DARTObj.from).getTime();
          const DARTTo = new Date(DARTObj.to).getTime();
          if (DARTObj.from && DARTObj.to && bookingDate >= DARTFrom && bookingDate <= DARTTo) {
            if (returnList.indexOf(fieldKey) === -1) {
              returnList.push(fieldKey);
            }
          }
        }
      }
    });
    return returnList;
  }

  showErrorForFields(fieldList: string[], formCollection: FormCollectionSet, errMsg: string) {
    fieldList.forEach(fieldLinkId => {
      Object.keys(formCollection).forEach(section => {
        const field = formCollection[section].data.find((item: BaseInput<any>) => item.linkId === fieldLinkId);
        if (field) {
          if (field.controlType !== 'label') {
            const errorObj = {};
            errorObj[errMsg] = true;
            formCollection[section].form.get(fieldLinkId).setErrors(errorObj);
          }
          field.invalid = true;
          field.errorMsg = errMsg;
        }
      });
    });
  }

  removeErrorForFormSection(section: string, formCollection: FormCollectionSet, errMsg: string, partial?: boolean) {
    let hasRace = false;
    let raceField: BaseInput<any> = null;
    formCollection[section].data.forEach((field: BaseInput<any>) => {
      const fieldErrors = Object.keys(formCollection[section].form.get(field.linkId)?.errors || {});
      if (field.linkId.startsWith('race-') ) {
        if (field.linkId === AppConstants.raceBlackLinkId) {
          raceField = field;
          if (!formCollection[section].form.get(field.linkId).invalid) {
            hasRace = true;
          }
        }
        if (!hasRace && formCollection[section].form.get(field.linkId).value) {
          hasRace = true;
        }
      } else if (field.errorMsg &&
        (field.controlType === 'label' || fieldErrors.includes(field.errorMsg)
        || fieldErrors.includes('required') && errMsg === 'A value is required to save.') &&
        (field.errorMsg === errMsg || (partial && field.errorMsg.includes(errMsg)))) {
        if (field.controlType !== 'label') {
          formCollection[section].form.get(field.linkId).updateValueAndValidity();
        }
        field.invalid = false;
        field.errorMsg = '';

      }
    });
    if (hasRace && formCollection[section].form.get(raceField.linkId)?.invalid) {
      formCollection[section].form.get(raceField.linkId).updateValueAndValidity();
      raceField.invalid = false;
      raceField.errorMsg = '';
    }
  }

  removeMultipleErrorsForFormSection(section: string, formCollection: FormCollectionSet, errArray: string[], partial?: boolean) {
    errArray?.forEach((errMsg: string) => {
      this.removeErrorForFormSection(section, formCollection, errMsg, partial);
    });
  }

  checkProcedures(formCollection: FormCollectionSet, section: string) {
    if (section === 'offlisting') {
      const SSPCIError = 'The type same sitting percutaneous coronary intervention indicated must also have a ' +
        'coronary angiogram indicated in the same entry.';
        
      const EPSTDYError = 'The type ablation indicated must also have a EP study indicated in the same entry.';
  
      const SSPCIFields = [];
      const EPSTDYFields = [];
      let coronaryAngiogramExist = false;
      let EPSTDYExist = false;
      this.removeErrorForFormSection('offlisting', formCollection, SSPCIError);
      this.removeErrorForFormSection('offlisting', formCollection, EPSTDYError);
      Object.keys(formCollection.offlisting.form.getRawValue()).forEach(fieldKey => {
        const fieldValue = formCollection.offlisting.form.getRawValue()[fieldKey];
        if (fieldValue) {
          if (fieldKey.includes(AppConstants.allProceduresLinkId)) {
            if (AppConstants.PCIForCoronaryAngiogram.includes(fieldValue)) {
              SSPCIFields.push(fieldKey); 
            } else if (fieldValue === AppConstants.proceduresCoronaryAngiogram) {
              coronaryAngiogramExist = true;
            }
            // #4362
            if(AppConstants.PCIForEPSTDY.includes(fieldValue)) {
              EPSTDYFields.push(fieldKey); 
            }
            else if (fieldValue === AppConstants.proceduresElectrophysiologyStudy) {
              EPSTDYExist = true;
            }
          }
        }
      });

      if (!coronaryAngiogramExist && SSPCIFields.length > 0) {
        this.showErrorForFields(SSPCIFields, formCollection, SSPCIError);
      }
      // #4362
      if (!EPSTDYExist && EPSTDYFields.length > 0) {
        this.showErrorForFields(EPSTDYFields, formCollection, EPSTDYError);
      }
    }
  }

  checkAllCrossFieldValidations(formCollection: FormCollectionSet, validators: CrossFieldValidationJson) {
    const crossFieldErrList = ['A Dates Affecting Readiness to Treat period', 'The value you have entered cannot be',
      'The value you have entered requires', 'must have STEMI as the Primary Reason for Referral Type.', 'A STEMI entry must have',
      'The Removal Date requires a Removal Reason' , 'The Removal Reason requires a Removal Date'];

    Object.keys(formCollection).forEach(sectionKey => {
      this.removeMultipleErrorsForFormSection(sectionKey, formCollection, crossFieldErrList, true);

      formCollection[sectionKey].data.forEach(field => {
        const fieldValue = formCollection[sectionKey].form.value[field.linkId];
        if (fieldValue) {
          this.checkCrossFieldValidation(fieldValue, field.linkId, sectionKey, formCollection, validators, field);
        }
      });
    });
  }

  checkCrossFieldValidation(
    value: string | DropDownOption, linkId: string, section: string, formCollection: FormCollectionSet,
    validators: CrossFieldValidationJson, field: BaseInput<any>) {
    const fieldToCheck = validators[section] ? validators[section][linkId] : null;
    let presentValue = '';

    if (fieldToCheck) {
      let errorMsg: string;

      if (fieldToCheck.min) {
        fieldToCheck.min.forEach((conditions: CrossFieldObject) => {
          let fieldValue = value;
          let valueToCheckAgainst = formCollection[conditions.section].form.value[conditions.linkId];
          let conditionDateTime: string;
          let valueTime: string;
          let dateTimeErrorMsg = '';

          // For the case, referral date min value is STEMI field value
          if (conditions.type === 'date') {
            const conditionField = formCollection[conditions.section].data
              .find((item: BaseInput<any>) => item.linkId === conditions.linkId);
            if (conditionField?.controlType === 'dateTimePicker') {
              dateTimeErrorMsg = ' Date';
            }
          }

          if (conditions.type === 'dateTime') {
            [fieldValue, valueToCheckAgainst, conditionDateTime, valueTime, dateTimeErrorMsg] =
              this.getDateTimeParams(section, formCollection, conditions, field);
          }

          if (valueToCheckAgainst) {
            if (this.checkFieldGreaterThanCondition(valueToCheckAgainst, fieldValue, conditions.type, conditionDateTime, valueTime)) {
              if (linkId === AppConstants.qualifyingElectrocardiogramDatetimeLinkId &&
                (conditions.linkId === AppConstants.registrationDateLinkId || conditions.linkId === AppConstants.firstHospitalEcgLinkId)) {
                  presentValue = formCollection[conditions.section].form.value[AppConstants.patientPresentationLinkId];
                  if (AppConstants.selfTransport.includes(presentValue)) {
                    // tslint:disable-next-line: max-line-length
                    errorMsg = 'The value you have entered cannot be before the ' + conditions.fieldName + dateTimeErrorMsg + '.';
                  }
              } else {
                errorMsg = 'The value you have entered cannot be before the ' + conditions.fieldName + dateTimeErrorMsg + '.';
              }           }
          }
        });
      }

      if (fieldToCheck.max) {
        fieldToCheck.max.forEach((conditions: CrossFieldObject) => {
          let fieldValue = value;
          let valueToCheckAgainst = formCollection[conditions.section].form.value[conditions.linkId];
          let conditionDateTime: string;
          let valueDateTime: string;
          let dateTimeErrorMsg = '';

          if (conditions.type === 'date') {
            const conditionField = formCollection[conditions.section].data
              .find((item: BaseInput<any>) => item.linkId === conditions.linkId);
            if (conditionField?.controlType === 'dateTimePicker') {
              dateTimeErrorMsg = ' Date';
            }
          }
          if (conditions.type === 'dateTime') {
            [fieldValue, valueToCheckAgainst, conditionDateTime, valueDateTime, dateTimeErrorMsg] =
              this.getDateTimeParams(section, formCollection, conditions, field);
          }
          if (valueToCheckAgainst) {
            // #2863, removal date doesn't allow 'equal to DART from date'
            // #2965, 'DART from/to date' compare with removal date, not allow equal
            if (linkId.startsWith(AppConstants.DARTFieldLinkId) && !conditions.linkId.startsWith(AppConstants.DARTFieldLinkId)) {
              if (this.checkFieldGreaterThanCondition(fieldValue, valueToCheckAgainst, conditions.type, valueDateTime, conditionDateTime, true)) {
                errorMsg = 'The value you have entered cannot be equal to or after the ' + conditions.fieldName + dateTimeErrorMsg + '.';
              }
              // #2965 Dart from compare with dart to, allows equal
              // or other date compare allows equal
            } else if (this.checkFieldGreaterThanCondition(fieldValue, valueToCheckAgainst, conditions.type, valueDateTime, conditionDateTime)) {
              // #3641
              if ((linkId === AppConstants.registrationDateLinkId || linkId === AppConstants.firstHospitalEcgLinkId)
                && conditions.linkId === AppConstants.qualifyingElectrocardiogramDatetimeLinkId) {
                  presentValue = formCollection[conditions.section].form.value[AppConstants.patientPresentationLinkId];
                  if (AppConstants.selfTransport.includes(presentValue)) {
                    errorMsg = 'The value you have entered cannot be after the ' + conditions.fieldName + dateTimeErrorMsg + '.';
                  }
              } else {
                errorMsg = 'The value you have entered cannot be after the ' + conditions.fieldName + dateTimeErrorMsg + '.';
              }
            }
          }
        });
      }

      if (fieldToCheck.exists && (field.controlType !== 'datepicker' || value.toString().toDate() !== null)) {
        fieldToCheck.exists.forEach((conditions: CrossFieldObject) => {
          const valueToCheckAgainst = formCollection[conditions.section].form.value[conditions.linkId];
          if (!valueToCheckAgainst) {
            errorMsg = this.fieldIsRequired(conditions);
          }
        });
      }

      if (fieldToCheck.filled) {
        fieldToCheck.filled.forEach((conditions: CrossFieldObject) => {
          const valueToCheckAgainst = formCollection[conditions.section]?.form.value[conditions.linkId];
          if (!valueToCheckAgainst) {
            const conditionField = formCollection[section].data.find((item: BaseInput<any>) => item.linkId === conditions.linkId);
            const errorObj = {};
            errorObj[conditions.customErrMsg] = true;
            formCollection[section]?.form.get(conditions.linkId).setErrors(errorObj);
            conditionField.errorMsg = this.setFieldError(formCollection[section].form, conditionField);
            formCollection[section]?.form.get(conditions.linkId).markAsTouched();
            conditionField.invalid = this.setInvalid(formCollection[section].form, conditionField);
          }
        });
      }

      if (fieldToCheck.equals) {
        fieldToCheck.equals.forEach((conditions: CrossFieldObject) => {
          errorMsg = this.checkEqualCrossField(formCollection, conditions, value) || errorMsg;
        });
      }

      if (fieldToCheck.notEqual) {
        fieldToCheck.notEqual.forEach((conditions: CrossFieldObject) => {
          errorMsg = this.checkEqualCrossField(formCollection, conditions, value, true) || errorMsg;
        });
      }

      if (fieldToCheck.match) {
        fieldToCheck.match.forEach((conditions: CrossFieldObject) => {
          const valueToCheckAgainst = formCollection[conditions.section].form.value[conditions.linkId];
          if (valueToCheckAgainst && valueToCheckAgainst === value) {
            errorMsg = 'The value you have entered cannot be equal to the ' + conditions.fieldName + '.';
          }
        });
      }

      if (errorMsg) {
        const errorObj = {};
        errorObj[errorMsg] = true;
        formCollection[section]?.form.get(linkId).setErrors(errorObj);
        field.errorMsg = errorMsg;
      }
      field.invalid = this.setInvalid(formCollection[section].form, field);
    }
  }

  getDateTimeParams(section: string, formCollection: FormCollectionSet, conditions: CrossFieldObject, field: BaseInput<any>) {
    const dateTimeField = field as DateTimeInput;
    const conditionField = formCollection[section].data.find((item: BaseInput<any>) => item.linkId === conditions.linkId);
    const conditionDateTime = formCollection[conditions.section].form.value[conditionField.linkId];
    const valueDateTime = formCollection[section].form.value[dateTimeField.linkId];
    // new STEMI fields do not separate Date and Time field any more and stored in the format: YYYY-MM-DDThh:mm:ss-zz:zz
    let dateTimeErrorMsg = '';
    if (valueDateTime && conditionDateTime && new Date(conditionDateTime) &&
    new Date(valueDateTime)) {
      dateTimeErrorMsg = ' Date and Time';
    }
    return [
      formCollection[conditions.section].form.value[dateTimeField.linkId],
      formCollection[conditions.section].form.value[conditionField.linkId],
      conditionDateTime,
      valueDateTime,
      dateTimeErrorMsg
    ];
  }

  checkFieldGreaterThanCondition(
    value: string | DropDownOption, condition: string | DropDownOption, type: string,
    valueTime?: string, conditionTime?: string, notAllowEqual?: boolean) {
    if (type === 'date') {
      const valueString = (value as string).includes('T') ? new Date(value as string).toLocaleDateString('en-CA') : value as string;
      const conditionString = (condition as string).includes('T') ? new Date(condition as string).toLocaleDateString('en-CA') :
        condition as string;
      const dateValue = new Date(valueString).getTime();
      const conditionValue = new Date(conditionString).getTime();
      if (notAllowEqual) {
        return dateValue >= conditionValue;
      } else {
        return dateValue > conditionValue;
      }
    } else if (type === 'number') {
      return +value > +condition;
    } else if (type === 'dateTime') {
      const valueDate = new Date(value as string);
      const conditionDate = new Date(condition as string);

      if (valueDate.getTime() === conditionDate.getTime()) {
        if (valueTime && conditionTime && this.utilService.checkValidTime(valueTime) && this.utilService.checkValidTime(conditionTime)) {
          const valueTimePair = this.utilService.getTimeParts(valueTime);
          const conditionTimePair = this.utilService.getTimeParts(conditionTime);

          valueDate.setHours(valueTimePair.hour, valueTimePair.minute);
          conditionDate.setHours(conditionTimePair.hour, conditionTimePair.minute);
        }
      }
      return valueDate.getTime() > conditionDate.getTime();

    }
  }

  checkEqualCrossField(
    formCollection: FormCollectionSet, conditions: CrossFieldObject, value: string | DropDownOption, notEqual?: boolean) {
    let errorMsg = '';
    const conditionField = formCollection[conditions.section].data.find(
      (item: BaseInput<any>) => item.show && item.linkId[0] === conditions.linkId[0] && item.linkId.includes(conditions.linkId));

    let valueToCheckAgainst: string;
    if (conditionField) {
      valueToCheckAgainst = formCollection[conditions.section]?.form.value[conditionField.linkId];
    }

    // check if this field's value is the same the field in the condition's value,
    // if notEqual tag is set to true, checks if this field's value is not the same as field in the condition's value
    if (((!valueToCheckAgainst || valueToCheckAgainst !== conditions.matchValue) !== !!notEqual) && value === conditions.value) {
      if (conditionField) {
        errorMsg = conditions.customErrMsg;
        const errorObj = {};
        errorObj[conditions.customErrMsg] = true;
        formCollection[conditions.section]?.form.get(conditionField.linkId).setErrors(errorObj);
        conditionField.errorMsg = this.setFieldError(formCollection[conditions.section].form, conditionField);
        formCollection[conditions.section]?.form.get(conditionField.linkId).markAsTouched();
        conditionField.invalid = this.setInvalid(formCollection[conditions.section].form, conditionField);
      } else {
        errorMsg = conditions.customErrMsg;
      }
    }
    return errorMsg;
  }

  fieldIsRequired(conditions: CrossFieldObject) {
    let requireString: string;

    const fieldName = conditions.fieldName.toLowerCase();
    if (fieldName.charAt(0) === ('a' || 'e' || 'i' || 'o' || 'u')) {
      requireString = 'The value you have entered requires an ';
    } else {
      requireString = 'The value you have entered requires a ';
    }
    const errorMsg = requireString + conditions.fieldName + '.';
    return errorMsg;
  }

  setFieldError(form: FormGroup, field: BaseInput<any>): string {
    let errorMsg: string;
    const invalidString = 'The value you have entered is invalid. ';
    if (form.controls[field.linkId].errors) {
      for (const error of Object.keys(form.controls[field.linkId].errors)) {
        if (error !== 'required' && error !== 'infield') {
          if (field.validation && field.validation.type === 'number') {
            switch (error) {
              case 'max':
              case 'min':
              case 'pattern':
                if (AppConstants.numericRangeIncludingUnknown?.includes(field.linkId)) {
                  errorMsg = invalidString + 'It must be between ' +
                    field.validation.min + ' and ' + field.validation.max + ', or Unknown.';
                } else {
                  errorMsg = invalidString + 'It must be between ' +
                    field.validation.min + ' and ' + field.validation.max + '.';
                }
                break;
              default:
                errorMsg = error;
                break;
            }
          } else if (field.validation && field.validation.type === 'date') {
            switch (error) {
              case 'max':
                errorMsg = 'The value you have entered cannot be after ' + field.validation.max + '.';
                break;
              case 'min':
                errorMsg = 'The value you have entered cannot be before ' + field.validation.min + '.';
                break;
              case 'curMax':
                errorMsg = 'The value you have entered cannot be in the future.';
                break;
              case 'curMin':
                errorMsg = 'The value you have entered cannot be before 1800-01-01.';
                break;
              case 'pattern':
                errorMsg = invalidString + (field.validation.patternDescription || '');
                break;
              default:
                errorMsg = error;
                break;
            }
          } else {
            errorMsg = error;
          }
        }
      }
    }
    return errorMsg;
  }

  setFieldWarning(form: FormGroup, field: BaseInput<any>): string {
    let warningMsg: string;
    const warnings = (form.controls[field.linkId] as AbstractControlWarn).warnings;
    if (warnings) {
      for (const warning of Object.keys(warnings)) {
        switch (warning) {
          case 'warningWithinMin':
            warningMsg = 'Warning: ' + this.utilService.getFieldNameFromLabel(field.label) + ' is not within ' +
              field.validation.warning.withinMin + ' days of the current date.';
            break;
        }
      }
    }
    return warningMsg;
  }

  setInvalid(form: FormGroup, field: BaseInput<any>) {
    return form.controls[field.linkId].invalid && field.errorMsg &&
      (form.controls[field.linkId].touched || form.controls[field.linkId].dirty);
  }

  setWarning(form: FormGroup, field: BaseInput<any>) {
    return field.warningMsg &&
      (form.controls[field.linkId].touched || form.controls[field.linkId].dirty);
  }

  bookingNotEqualRemovalDate(activeBooking: string, removalDate?: string) {
    let currentRemovalDate: number;
    let currentBookingDate: number;
    if (removalDate && activeBooking) {
      currentRemovalDate = new Date(removalDate as string).getTime();
      currentBookingDate = new Date(activeBooking as string).getTime();
    }
    return currentRemovalDate !== currentBookingDate;
  }

  checkActiveBookingAndRemovalDate(formCollection: FormCollectionSet, linkId: string) {
    let bookingValue = null;
    const bookingField = this.getActiveBookingDate(formCollection);
    const removalDateErr = 'The value you have entered cannot be before or after the Booking Date.';
    const bookingErr = 'The value you have entered cannot be before or after the Removal Date.';
    const noActiveBookingsErr = 'The value you have entered requires a Booking Date that has not been cancelled.';
    const acceptanceRequiredErr = 'The value you have entered requires an Acceptance Date.';
    const removalReasonErr = 'The value you have entered requires the Removal Date to be the same as Booking Date.';

    if (bookingField) {
      bookingValue = formCollection.acceptance.form.get(bookingField.linkId).value;
    }

    if (formCollection.offlisting?.form.get('removal-reason').value === 'PS') {
      const acceptanceDate = formCollection.acceptance.form.get('acceptance-date').value;
      const removalDate = formCollection.offlisting.form.get('removal-date').value;

      if (!acceptanceDate) {
        this.showErrorForFields(['removal-reason'], formCollection, acceptanceRequiredErr);
      }
      if (removalDate && bookingField && bookingValue && this.bookingNotEqualRemovalDate(removalDate, bookingValue)) {
        this.showErrorForFields([bookingField.linkId], formCollection, bookingErr);
        this.showErrorForFields(['removal-date'], formCollection, removalDateErr);
        this.showErrorForFields(['removal-reason'], formCollection, removalReasonErr);
      } else if (removalDate && !bookingField) {
        this.showErrorForFields(['removal-date'], formCollection, noActiveBookingsErr);
        this.showErrorForFields(['removal-reason'], formCollection, noActiveBookingsErr);
      } else if (!removalDate && !bookingField) {
        this.showErrorForFields(['removal-reason'], formCollection, noActiveBookingsErr);
      }
    }
  }

  getActiveBookingDate(formCollection: FormCollectionSet): BaseInput<any> {
    for (const field of formCollection.acceptance.data) {
      const fieldValue = formCollection.acceptance.form.value[field.linkId];
      if (fieldValue && field.linkId.includes('cancellations-and-bookings-booking-date')) {
        return field;
      }
    }
    return null;
  }
}
